import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../lib/api';

/**
 * Netflix-style horizontal slider used across the home for multiple feeds
 * (trending, latest releases, top rated, …).
 *
 * Props:
 *   - endpoint: API path relative to `/api` (e.g. "/tmdb/trending/movie")
 *   - testKey: string used as data-testid namespace (e.g. "movie", "latest")
 *   - title: section headline
 *   - emoji: leading emoji in the headline
 *   - mediaTypeFallback (optional): 'movie' | 'tv' — used if server items lack `media_type`
 */
export default function TrendingSlider({ endpoint, testKey, title, emoji, mediaTypeFallback }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    api.get(endpoint)
      .then(({ data }) => {
        if (cancel) return;
        setItems(data.items || []);
        setStale(!!data.stale);
      })
      .catch(() => { if (!cancel) setError(true); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [endpoint]);

  const scrollBy = (dx) => {
    if (scrollerRef.current) scrollerRef.current.scrollBy({ left: dx, behavior: 'smooth' });
  };

  return (
    <section className="mt-8" data-testid={`trending-${testKey}-section`}>
      <div className="px-5 flex items-center justify-between mb-1">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1.5">
            <RefreshCw className="w-2.5 h-2.5" />
            {stale ? 'Cache in fallback · TMDB' : 'Aggiornato automaticamente da TMDB'}
          </span>
          <h3 className="font-heading text-xl font-light mt-0.5">
            <span className="mr-1.5">{emoji}</span>{title}
          </h3>
        </div>
        <div className="hidden sm:flex items-center gap-1">
          <button
            onClick={() => scrollBy(-320)}
            className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-[var(--gold)]/50 hover:text-[var(--gold)] transition-colors"
            aria-label="Scorri indietro"
            data-testid={`trending-${testKey}-prev`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scrollBy(320)}
            className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-[var(--gold)]/50 hover:text-[var(--gold)] transition-colors"
            aria-label="Scorri avanti"
            data-testid={`trending-${testKey}-next`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="mt-3 flex gap-3 overflow-x-auto scroll-smooth px-5 pb-3 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        data-testid={`trending-${testKey}-scroller`}
      >
        <style>{`section[data-testid="trending-${testKey}-section"] > div::-webkit-scrollbar{display:none;}`}</style>

        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex-shrink-0 w-[130px] snap-start">
            <div className="w-[130px] h-[195px] rounded-xl bg-white/5 animate-pulse" />
            <div className="mt-2 h-3 w-3/4 rounded bg-white/5 animate-pulse" />
            <div className="mt-1 h-2 w-1/2 rounded bg-white/5 animate-pulse" />
          </div>
        ))}

        {!loading && error && (
          <div className="w-full text-center text-xs text-white/40 py-6">
            Impossibile caricare i contenuti. Riprova più tardi.
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="w-full text-center text-xs text-white/40 py-6">
            Nessun contenuto disponibile al momento.
          </div>
        )}

        {!loading && !error && items.map((it, i) => {
          const media = it.media_type || mediaTypeFallback || 'movie';
          return (
            <motion.div
              key={`${media}-${it.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i, 8) * 0.04, duration: 0.35 }}
              className="flex-shrink-0 w-[130px] snap-start"
            >
              <Link
                to={`/tmdb/${media}/${it.id}`}
                data-testid={`trending-card-${testKey}-${it.id}`}
                className="block group"
              >
                <div className="relative w-[130px] h-[195px] rounded-xl overflow-hidden border border-white/5 group-hover:border-[var(--gold)]/60 transition-all shadow-lg shadow-black/40">
                  {it.poster ? (
                    <img
                      src={it.poster}
                      alt={it.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 active:scale-95"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-white/5 flex items-center justify-center text-[10px] text-white/30 px-2 text-center">{it.title}</div>
                  )}
                  <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-[10px] flex items-center gap-0.5">
                    <Star className="w-2.5 h-2.5 text-[var(--gold)] fill-[var(--gold)]" />
                    <span className="tabular-nums text-white/90">{it.vote?.toFixed ? it.vote.toFixed(1) : it.vote}</span>
                  </div>
                  <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-white/70">
                    {media === 'tv' ? 'Serie TV' : 'Film'}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                </div>
                <div className="mt-2 px-0.5">
                  <div className="text-xs font-medium leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{it.title}</div>
                  <div className="text-[10px] text-white/50 mt-0.5 tabular-nums">{it.year || '—'}</div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
